import { Nullable } from '@/shared/types/app.types'
import { Control } from 'ol/control'
import { Options } from 'ol/control/Control'
import { Extent,
    containsExtent,
    equals as equalsExtent,
    getBottomRight,
    getTopLeft,
    scaleFromCenter
} from 'ol/extent'
import Layer from 'ol/layer/Layer'
import Overlay from 'ol/Overlay'
import PluggableMap from 'ol/PluggableMap'
import CompositeMapRenderer from 'ol/renderer/Composite'
import View from 'ol/View'
import OverlayPositioning from 'ol/OverlayPositioning'
import Map from 'ol/Map'
import MapProperty from 'ol/MapProperty'
import ObjectEventType from 'ol/ObjectEventType'
import { ObjectEvent } from 'ol/Object'
import ViewProperty from 'ol/ViewProperty'
import { EventsKey, listen, listenOnce } from 'ol/events'
import { fromExtent as polygonFromExtent } from 'ol/geom/Polygon'
import BaseEvent from 'ol/events/Event'
import MapEvent from 'ol/MapEvent'
import MapEventType from 'ol/MapEventType'
import { CLASS_COLLAPSED, CLASS_CONTROL, CLASS_UNSELECTABLE } from 'ol/css'
import { replaceNode } from 'ol/dom'

class ControlledMap extends PluggableMap {
    createRenderer(): CompositeMapRenderer {
        return new CompositeMapRenderer(this)
    }
}

export interface OverviewMapCustomOptions extends Options{
    view?: View
    layers: [Layer<any>]
    rotateWithView: boolean
    collapsed: boolean
    collapsible: boolean
    collapseLabel?: string
    label?: string
    tipLabel?: string
    className?:string
}

/*
This behavior is based on the oficcial OverviewMap
https://github.com/openlayers/openlayers/blob/v6.9.0/src/ol/control/OverviewMap.js
We need to take into account several differences like div parenting, mark old positioning, and so.
These differences create the need to bring here the code and modify the behavior, as the OOP design of this class doesn't allow to modify properly for the requirements we have
*/
export class OverViewMapCustom extends Control {
    private viewExtent: Nullable<Extent>
    private ovmapDiv: Nullable<HTMLElement>
    private ovmap: ControlledMap
    private view: Nullable<View>
    private overlay: Nullable<Overlay>
    private overlayBox: Nullable<HTMLElement>
    private rotateWithView: boolean
    private boundHandleRotationChanged:()=>void
    private ovmapPostrenderKey: Nullable<EventsKey>
    private collapsed: boolean
    private collapseLabel: Nullable<HTMLElement>
    private label: Nullable<HTMLElement>
    private collapsible:boolean
    private listenerMouseMove: (event: MouseEvent) => void = (event: MouseEvent) => { this.move(event) }
    private listenerMouseUp: (event: MouseEvent) => void = (event: MouseEvent) => { this.endMoving(event) }

    /**
     * Maximum width and/or height extent ratio that determines when the overview
     * map should be zoomed out.
     */
    private MAX_RATIO = 0.75;

    /**
     * Minimum width and/or height extent ratio that determines when the overview
     * map should be zoomed in.
     */
    private MIN_RATIO = 0.1;

    constructor(overviewBox: HTMLElement, overviewMap:HTMLElement, options: OverviewMapCustomOptions) {
        super({
            element: overviewBox,
            render: options.render,
            target: options.target
        })

        this.ovmapDiv = overviewMap
        this.view = options.view
        this.rotateWithView = options.rotateWithView
        this.collapsed = options.collapsed
        this.collapsible = options.collapsible

        this.initializeLabels(options)

        this.boundHandleRotationChanged = this.handleRotationChanged.bind(this)

        this.ovmap = new ControlledMap({
            view: options.view ?? undefined
        })
        if (options.layers) {
            options.layers.forEach((layer) => {
                this.ovmap.addLayer(layer)
            })
        }

        this.initializeOverlay()
    }

    private initializeLabels(options: OverviewMapCustomOptions):void {
        const collapseLabel = options.collapseLabel ?? '\u2039'

        if (typeof collapseLabel === 'string') {
            this.collapseLabel = document.createElement('span')
            this.collapseLabel.textContent = collapseLabel
        } else {
            this.collapseLabel = collapseLabel
        }
        const label = options.label !== undefined ? options.label : '\u203A'
        if (typeof label === 'string') {
            this.label = document.createElement('span')
            this.label.textContent = label
        } else {
            this.label = label
        }
        const activeLabel = this.collapsible && !this.collapsed ? this.collapseLabel : this.label
        const button = document.createElement('button')
        button.setAttribute('type', 'button')
        button.title = options.tipLabel ?? ''
        button.appendChild(activeLabel)
        button.addEventListener(
            'click',
            this.handleClick.bind(this),
            false
        )
    }

    private initializeOverlay():void {
        const box = document.createElement('div')
        box.className = 'ol-overviewmap-box'
        box.style.boxSizing = 'border-box'

        this.overlay = new Overlay({
            position: [0, 0],
            positioning: OverlayPositioning.CENTER_CENTER,
            element: box
        })
        this.overlayBox = this.overlay.getElement()
        if (!this.overlayBox) return
        this.ovmap.addOverlay(this.overlay)

        this.overlayBox.addEventListener('mousedown', () => {
            window.addEventListener('mousemove', this.listenerMouseMove)
            window.addEventListener('mouseup', this.listenerMouseUp)
        })
    }

    private computeDesiredMousePosition(mousePosition:MouseEvent):MouseEvent {
        return {
            clientX: mousePosition.clientX,
            clientY: mousePosition.clientY
        } as MouseEvent
    }

    private move(event:MouseEvent):void {
        const position = this.computeDesiredMousePosition(event)
        const coordinates = this.ovmap.getEventCoordinateInternal(position)
        this.overlay?.setPosition(coordinates)

        const pluggableMap = this.getMap()

        const parentBounding = this.overlayBox?.parentElement?.parentElement?.getBoundingClientRect()
        const boxBounding = this.overlayBox?.getBoundingClientRect()
        if (parentBounding && boxBounding && pluggableMap) {
            const pos = { left: boxBounding?.left - parentBounding.left, top: boxBounding?.top - parentBounding?.top }

            if (pos.left > 0 && pos.top > 0 &&
                pos.left < parentBounding?.width - boxBounding.width &&
                pos.top < parentBounding?.height - boxBounding.height) {
                pluggableMap.getView().setCenterInternal(coordinates)
            }
        }
    }

    private endMoving(event:MouseEvent):void {
        window.removeEventListener('mousemove', this.listenerMouseMove)
        window.removeEventListener('mouseup', this.listenerMouseUp)
    }

    /**
     * Remove the control from its current map and attach it to the new map.
     * Subclasses may set up event handlers to get notified about changes to
     * the map here.
     */
    public setMap(map:Map):void {
        const oldMap = this.getMap()
        if (map === oldMap) {
            return
        }
        if (oldMap) {
            const oldView = oldMap.getView()
            if (oldView) {
                this.unbindView(oldView)
            }
            this.ovmap.setTarget()
        }
        super.setMap(map)

        if (map && this.ovmapDiv) {
            this.ovmap.setTarget(this.ovmapDiv)
            this.listenerKeys.push(
                listen(
                    map,
                    ObjectEventType.PROPERTYCHANGE,
                    this.handleMapPropertyChange,
                    this
                )
            )

            const view = map.getView()
            if (view) {
                this.bindView(view)
                if (view.isDef()) {
                    this.ovmap.updateSize()
                    this.resetExtent()
                }
            }

            if (!this.ovmap.isRendered()) {
                this.updateBoxAfterOvmapIsRendered()
            }
        }
    }

    /**
     * Handle map property changes.  This only deals with changes to the map's view.
     */
    private handleMapPropertyChange(event_: BaseEvent | Event): boolean {
        const event = event_ as ObjectEvent
        if (event.key === MapProperty.VIEW) {
            const oldView = event.oldValue
            if (oldView) {
                this.unbindView(oldView)
            }

            const newMap = this.getMap()
            if (newMap) {
                const newView = newMap.getView()
                this.bindView(newView)
            }
        } else if (
            !this.ovmap.isRendered() &&
            (event.key === MapProperty.TARGET || event.key === MapProperty.SIZE)
        ) {
            this.ovmap.updateSize()
        }

        // TODO check
        return true
    }

    /**
     * Register listeners for view property changes.
     */
    private bindView(view:View):void {
        if (!this.view) {
            // Unless an explicit view definition was given, derive default from whatever main map uses.
            const newView = new View({
                projection: view.getProjection()
            })
            this.ovmap.setView(newView)
        }

        view.addChangeListener(
            ViewProperty.ROTATION,
            this.boundHandleRotationChanged
        )
        // Sync once with the new view
        this.handleRotationChanged()
    }

    /**
     * Unregister listeners for view property changes.
     */
    private unbindView(view:View):void {
        view.removeChangeListener(
            ViewProperty.ROTATION,
            this.boundHandleRotationChanged
        )
    }

    /**
     * Handle rotation changes to the main map.
     */
    private handleRotationChanged():void {
        if (this.rotateWithView) {
            const currentMap = this.getMap()
            if (currentMap) {
                this.ovmap.getView().setRotation(currentMap.getView().getRotation())
            }
        }
    }

    /**
     * Reset the overview map extent if the box size (width or
     * height) is less than the size of the overview map size times minRatio
     * or is greater than the size of the overview size times maxRatio.
     *
     * If the map extent was not reset, the box size can fits in the defined
     * ratio sizes. This method then checks if is contained inside the overview
     * map current extent. If not, recenter the overview map to the current
     * main map center location.
     */
    validateExtent():void {
        const map = this.getMap()
        const ovmap = this.ovmap

        if (!map || !map.isRendered() || !ovmap.isRendered()) {
            return
        }

        const mapSize = map.getSize()
        const view = map.getView()
        const extent = view.calculateExtentInternal(mapSize)

        if (this.viewExtent && equalsExtent(extent, this.viewExtent)) {
            // repeats of the same extent may indicate constraint conflicts leading to an endless cycle
            return
        }
        this.viewExtent = extent

        const ovmapSize = ovmap.getSize()
        if (!ovmapSize) {
            return
        }

        const ovview = ovmap.getView()
        const ovextent = ovview.calculateExtentInternal(ovmapSize)

        const topLeftPixel = ovmap.getPixelFromCoordinateInternal(
            getTopLeft(extent)
        )
        const bottomRightPixel = ovmap.getPixelFromCoordinateInternal(
            getBottomRight(extent)
        )

        const boxWidth = Math.abs(topLeftPixel[0] - bottomRightPixel[0])
        const boxHeight = Math.abs(topLeftPixel[1] - bottomRightPixel[1])

        const ovmapWidth = ovmapSize[0]
        const ovmapHeight = ovmapSize[1]

        if (
            boxWidth < ovmapWidth * this.MIN_RATIO ||
            boxHeight < ovmapHeight * this.MIN_RATIO ||
            boxWidth > ovmapWidth * this.MAX_RATIO ||
            boxHeight > ovmapHeight * this.MAX_RATIO
        ) {
            this.resetExtent()
        } else if (!containsExtent(ovextent, extent)) {
            this.recenter()
        }
    }

    /**
     * Reset the overview map extent to half calculated min and max ratio times
     * the extent of the main map.
     */
    private resetExtent():void {
        if (this.MAX_RATIO === 0 || this.MIN_RATIO === 0) {
            return
        }

        const map = this.getMap()
        if (!map) return
        const ovmap = this.ovmap

        const mapSize = map.getSize()

        const view = map.getView()
        const extent = view.calculateExtentInternal(mapSize)

        const ovview = ovmap.getView()

        // get how many times the current map overview could hold different
        // box sizes using the min and max ratio, pick the step in the middle used
        // to calculate the extent from the main map to set it to the overview map,
        const steps = Math.log(this.MAX_RATIO / this.MIN_RATIO) / Math.LN2
        const ratio = 1 / (Math.pow(2, steps / 2) * this.MIN_RATIO)
        scaleFromCenter(extent, ratio)
        ovview.fitInternal(polygonFromExtent(extent))
    }

    /**
     * Set the center of the overview map to the map center without changing its
     * resolution.
     */
    private recenter():void {
        const map = this.getMap()
        if (!map) return
        const ovmap = this.ovmap

        const view = map.getView()

        const ovview = ovmap.getView()

        ovview.setCenterInternal(view.getCenterInternal())
    }

    /**
     * Update the box using the main map extent
     */
    private updateBox(): void {
        const map = this.getMap()
        const ovmap = this.ovmap

        if (!map || !map.isRendered() || !ovmap.isRendered()) {
            return
        }

        const mapSize = map.getSize()
        if (!mapSize) return

        const view = map.getView()

        const ovview = ovmap.getView()

        const rotation = this.rotateWithView ? 0 : -view.getRotation()

        const overlay = this.overlay
        const box = this.overlay?.getElement()
        const center = view.getCenterInternal()
        const resolution = view.getResolution()
        if (!resolution) return
        const ovresolution = ovview.getResolution()
        if (!ovresolution) return
        const width = (mapSize[0] * resolution) / ovresolution
        const height = (mapSize[1] * resolution) / ovresolution

        // set position using center coordinates
        overlay?.setPosition(center)

        // set box size calculated from map extent size and overview map resolution
        if (box) {
            box.style.width = width + 'px'
            box.style.height = height + 'px'
            const transform = 'rotate(' + rotation + 'rad)'
            box.style.transform = transform
        }
    }

    private updateBoxAfterOvmapIsRendered():void {
        if (this.ovmapPostrenderKey) {
            return
        }
        this.ovmapPostrenderKey = listenOnce(
            this.ovmap,
            MapEventType.POSTRENDER,
            (event) => {
                delete this.ovmapPostrenderKey
                this.updateBox()
            },
            this
        )
    }

    private handleClick(event: MouseEvent):void {
        event.preventDefault()
        this.handleToggle()
    }

    private handleToggle():void {
        this.element.classList.toggle(CLASS_COLLAPSED)
        if (this.collapsed && this.collapseLabel && this.label) {
            replaceNode(this.collapseLabel, this.label)
        } else if (this.label && this.collapseLabel) {
            replaceNode(this.label, this.collapseLabel)
        }
        this.collapsed = !this.collapsed

        // manage overview map if it had not been rendered before and control
        // is expanded
        const ovmap = this.ovmap
        if (!this.collapsed) {
            if (ovmap.isRendered()) {
                this.viewExtent = undefined
                ovmap.render()
                return
            }
            ovmap.updateSize()
            this.resetExtent()
            this.updateBoxAfterOvmapIsRendered()
        }
    }

    public getCollapsible():boolean {
        return this.collapsible
    }

    public setCollapsible(collapsible:boolean):void {
        if (this.collapsible === collapsible) {
            return
        }
        this.collapsible = collapsible
        this.element.classList.toggle('ol-uncollapsible')
        if (!collapsible && this.collapsed) {
            this.handleToggle()
        }
    }

    public setCollapsed(collapsed:boolean):void {
        if (!this.collapsible || this.collapsed === collapsed) {
            return
        }
        this.handleToggle()
    }

    public getCollapsed():boolean {
        return this.collapsed
    }

    public getRotateWithView():boolean {
        return this.rotateWithView
    }

    public setRotateWithView(rotateWithView:boolean):void {
        if (this.rotateWithView === rotateWithView) {
            return
        }
        this.rotateWithView = rotateWithView
        const currentMap = this.getMap()
        if (!currentMap) return
        if (currentMap.getView().getRotation() !== 0) {
            if (this.rotateWithView) {
                this.handleRotationChanged()
            } else {
                this.ovmap.getView().setRotation(0)
            }
            this.viewExtent = undefined
            this.validateExtent()
            this.updateBox()
        }
    }

    public getOverviewMap():Map {
        return this.ovmap
    }

    public render(mapEvent:MapEvent):void {
        this.validateExtent()
        this.updateBox()
    }
}
